import { UseFormReturn } from 'react-hook-form';

import React, { useEffect, useMemo, useState } from 'react';
import { MiscellaneousField } from '../../reportsTypes';
import { FieldEnabledState } from '../Miscellaneous/MiscellaneousStringItem';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';
import { groupFieldsByDependency } from '../utils/groupedFields';
import { FieldGroupRenderer } from '../Miscellaneous/FieldGroupRenderer';
import { DateRange } from 'react-day-picker';

interface MiscellaneousFormFieldProps {
  form: UseFormReturn<ReportCloneValues>;
  miscellaneousFields: Record<string, MiscellaneousField>;
  excludeFields?: string[];
  dateRange?: DateRange;
}

export const MiscellaneousFormField = ({
  form,
  miscellaneousFields,
  excludeFields,
  dateRange,
}: MiscellaneousFormFieldProps) => {
  const filteredFields = useMemo(() => {
    if (!excludeFields || excludeFields.length === 0)
      return miscellaneousFields;
    return Object.fromEntries(
      Object.entries(miscellaneousFields).filter(
        ([key]) => !excludeFields.includes(key),
      ),
    );
  }, [miscellaneousFields, excludeFields]);
  const [watchedValues, setWatchedValues] = useState<Record<string, unknown>>(
    () => form.getValues(),
  );

  const [fieldEnabledState, setFieldEnabledState] = useState<FieldEnabledState>(
    () => {
      const initialState: FieldEnabledState = {};
      Object.keys(filteredFields).forEach((fieldName) => {
        const fieldInfo = filteredFields[fieldName];
        initialState[fieldName] =
          !fieldInfo.enabledOn ||
          (fieldInfo.enabledOn &&
            (form.getValues()['miscellaneous'] as Record<string, unknown>)?.[
              fieldInfo.enabledOn.field
            ] === fieldInfo.enabledOn.value);
      });
      return initialState;
    },
  );

  useEffect(() => {
    const subscription = form.watch((values: Partial<ReportCloneValues>) => {
      setWatchedValues(values as Record<string, unknown>);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    let hasChanged = false;
    const updatedState: FieldEnabledState = { ...fieldEnabledState };

    Object.keys(filteredFields).forEach((fieldName) => {
      const fieldInfo = filteredFields[fieldName];

      // Check if watchedValues has the key and if it's equal to the expected value
      const isEnabled =
        !fieldInfo.enabledOn ||
        (fieldInfo.enabledOn &&
          (watchedValues['miscellaneous'] as Record<string, unknown>)?.[
            fieldInfo.enabledOn.field
          ] === fieldInfo.enabledOn.value);

      if (updatedState[fieldName] !== isEnabled) {
        updatedState[fieldName] = isEnabled;
        hasChanged = true;

        if (!isEnabled && fieldEnabledState[fieldName]) {
          form.setValue('miscellaneous.' + fieldName, null);
        } else {
          form.setValue('miscellaneous.' + fieldName, fieldInfo.defaultValue);
        }
      }
    });

    if (hasChanged) {
      setFieldEnabledState(updatedState);
    }
  }, [form, watchedValues, fieldEnabledState, filteredFields]);

  const groupedFields = groupFieldsByDependency(filteredFields);

  return (
    <div className="space-y-6">
      {groupedFields.length > 0 &&
        groupedFields.map((group) => (
          <FieldGroupRenderer
            key={group.join('-')}
            group={group}
            miscellaneousFields={filteredFields}
            fieldEnabledState={fieldEnabledState}
            form={form}
            prefix={true}
            dateRange={dateRange}
          />
        ))}
    </div>
  );
};
